/**
 * Frontend Order System - Main Application Component
 */

import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import ProductOrderInterface from './ProductOrderInterface';
import GuestCheckoutModal from './GuestCheckoutModal';

const FrontendOrderApp = ({ productId, _isShortcode = false, localization = {} }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [productInfo, setProductInfo] = useState(null);
    const [warehouseInfo, setWarehouseInfo] = useState(null);
    const [showGuestCheckout, setShowGuestCheckout] = useState(false);
    const [, setGuestInfo] = useState({});
    
    // Check if user is guest
    const isGuest = !localization.isUserLoggedIn;
    const isMobile = window.innerWidth <= 767;
    
    /**
     * Initialize component - load product and warehouse information
     */
    useEffect(() => {
        initializeApp();
    }, [productId]);
    
    const initializeApp = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Get current product info from localization or global
            const currentProduct = localization.currentProduct;
            if (currentProduct && currentProduct.id == productId) {
                setProductInfo(currentProduct);
            } else {
                // Fallback: get product info from WooCommerce globals if available
                const fallbackProduct = {
                    id: productId,
                    name: `Product #${productId}`,
                    price: 0,
                    type: 'simple',
                    is_purchasable: true,
                    is_in_stock: true
                };
                setProductInfo(fallbackProduct);
            }
            
            // Load warehouse information
            const warehouseResponse = await fetch(
                `${localization.apiUrl}product/${productId}/warehouse-info`,
                {
                    headers: {
                        'X-WP-Nonce': localization.nonce
                    }
                }
            );
            
            if (!warehouseResponse.ok) {
                throw new Error(`HTTP ${warehouseResponse.status}: ${warehouseResponse.statusText}`);
            }
            
            const warehouseData = await warehouseResponse.json();
            setWarehouseInfo(warehouseData);
            
        } catch (err) {
            setError(err.message || __('載入商品信息時發生錯誤', 'frontend-order-system'));
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Handle guest checkout modal
     */
    const handleGuestCheckout = (cartData) => {
        if (isGuest) {
            setShowGuestCheckout(true);
        } else {
            // For logged-in users, proceed directly to checkout
            proceedToCheckout(cartData);
        }
    };
    
    /**
     * Handle guest info submission
     */
    const handleGuestInfoSubmitted = (guestData) => {
        setGuestInfo(guestData);
        setShowGuestCheckout(false);
        // Proceed to checkout with guest info
        proceedToCheckout(null, guestData);
    };
    
    /**
     * Proceed to WooCommerce checkout
     */
    const proceedToCheckout = (_cartData = null, _guestData = null) => {
        if (localization.woocommerce && localization.woocommerce.checkout_url) {
            window.location.href = localization.woocommerce.checkout_url;
        } else {
            window.location.href = '/checkout/';
        }
    };
    
    /**
     * Handle errors with user-friendly messages
     */
    const handleError = (error, _context = '') => {
        setError(error.message || error || __('發生未知錯誤', 'frontend-order-system'));
    };
    
    /**
     * Render loading state
     */
    if (loading) {
        return (
            <div className="frontend-order-app">
                <div className="fos-header">
                    <p>{__('選擇您的庫存位置', 'frontend-order-system')}</p>
                </div>
                
                <div className="fos-loading">
                    <div className="spinner"></div>
                    <span>{__('正在載入商品信息...', 'frontend-order-system')}</span>
                </div>
            </div>
        );
    }
    
    /**
     * Render error state
     */
    if (error) {
        return (
            <div className="frontend-order-app">
                <div className="fos-header">
                    <p>{__('選擇您的庫存位置', 'frontend-order-system')}</p>
                </div>
                
                <div className="fos-error">
                    <span className="fos-error-icon">⚠️</span>
                    {error}
                </div>
                
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <button 
                        className="fos-btn fos-btn-secondary"
                        onClick={initializeApp}
                    >
                        {__('重試', 'frontend-order-system')}
                    </button>
                </div>
            </div>
        );
    }
    
    /**
     * Render main application
     */
    return (
        <div className={`frontend-order-app ${isMobile ? 'fos-mobile' : 'fos-desktop'}`}>
            {/* Header */}
            <div className="fos-header">
                <p>{__('選擇您的庫存位置', 'frontend-order-system')}</p>
            </div>
            
            
            {/* Main Order Interface */}
            {warehouseInfo && (
                <ProductOrderInterface
                    productId={productId}
                    productInfo={productInfo}
                    warehouseInfo={warehouseInfo}
                    isGuest={isGuest}
                    isMobile={isMobile}
                    localization={localization}
                    onError={handleError}
                    onGuestCheckout={handleGuestCheckout}
                />
            )}
            
            {/* Guest Checkout Modal */}
            {showGuestCheckout && (
                <GuestCheckoutModal
                    isOpen={showGuestCheckout}
                    onClose={() => setShowGuestCheckout(false)}
                    onSubmit={handleGuestInfoSubmitted}
                    localization={localization}
                />
            )}
            
        </div>
    );
};

export default FrontendOrderApp;
